/**
 * content-detect.js — shared speech-vs-music classifier + whole-file noise-floor.
 *
 * Ported from /audio-enhancer's `detectContent` (calibrated on real songs
 * 2026-06-12, bug #732 Alexandr — see that blade for the calibration story).
 * Extracted to a shared primitive 2026-06-19 so /voice-enhancer reuses the
 * SAME "is this music?" verdict instead of a parallel classifier (the
 * voice-enhancer music-degradation class, comment #768 Андрей Крапивин).
 *
 * Pure function: no globals, no DOM, no telemetry side effects — the caller
 * decides what to do with the result and what to log. Cost ≈ one decodeAudioData
 * (stride-4 Goertzel over ≤3×5 s windows + one whole-file RMS pass).
 *
 *   ContentDetect.analyze(audioBuffer) → {
 *     content:        'music' | 'speech',
 *     musicScore:     number,        // 0..1, isMusic = score >= 0.45
 *     silentRatio, highRatio, lowBandRatio, transientRatio,  // raw features
 *     stereoDiff:     number|null,   // 0 = dual-mono, →1 = decorrelated stereo
 *     noiseFloorDb:   number|null,   // dBFS p05; null = not trustworthy
 *     noiseFloorRefDb:number|null,
 *     durationS:      number
 *   }
 *
 * The classifier is silentRatio-DOMINANT (the only feature that cleanly
 * separates the classes — music 1-10 % silent frames vs speech 25-30 %).
 * High-band / low-band / transients / stereo are additive confidence boosters.
 * Threshold 0.45. False positives (speech → music) are non-catastrophic for the
 * consumers (music path is gentler, never destructive).
 *
 * (The paragraph above is the 2026-06 design; v3 replaced silentRatio with
 * rhythm + sustained-pitch evidence — see the scorer — and v4 (2026-09-12)
 * added `vocal.gridShare`: the energy-weighted share of pitched frames on the
 * semitone grid, the one marker BEATLESS music has and speech cannot make.
 * Consumers: /voice-enhancer, /audio-enhancer, /video-audio, /denoise — the
 * verdict decides whether a speech-trained denoiser (RNNoise / DeepFilterNet)
 * may touch the file at all. Guard: tests/playwright/content-detect-corpus.mjs
 * incl. the solo-instrument and speech-plus-tone fixtures.)
 */
(function () {
    'use strict';

    /* ── v5 (2026-09-25): a SECOND OPINION for the uncertain band ──────────
       The hand scorer above cannot tell a quiet acoustic song (singer +
       guitar, no beat, pitches just under its grid tiers) from speech: 7 of
       67 songs on the labelled corpus read as speech, and every extra hand
       feature tried — 4 Hz modulation, spectral stationarity, chroma
       concentration, key spread — overlapped speech (a tone under speech is
       one pitch class; a monotone speaker sits in a key). YAMNet does: on the
       same 187 files speech ≤0.010 music-probability (incl. speech + tone /
       hum), songs ≥0.155 — a music BED under narration is the one case it
       hears as music, hence the ratio rule — int8, in a worker on
       onnxruntime-web WASM (public/js/yamnet-classify-worker.js).

       refine(buf, base) resolves the SAME shape as analyze(), with `deep`
       {speech, music, patches, ms} and `content` overridden by the model when
       the hand score sat in the band [0.20, 0.55): the corpus' misses live at
       0.3–0.4 and its speech tops out at 0.30, so outside the band the hand
       verdict stands and nothing is loaded. The verdict is music only when
       music ≥ 0.10 AND music ≥ 4 × speech (see DEEP_RATIO). Fail-open everywhere: no config,
       no worker, a timeout (default 30 s) → the base result, untouched.
       Configure once per page: ContentDetect.configureDeep({workerUrl,
       ortUrl, wasmBase, modelUrl}) — the blade partial
       tools/partials/content-detect-deep does it with asset-router URLs.
       The partial also PUBLISHES its config as window.__contentDetectDeep()
       and refine() takes it from there when nobody configured this module
       (2026-09-26): the partial's own push runs at DOMContentLoaded, and a
       copy of this file that failed its first load arrives later through the
       fallback ladder — the push had already found no module and never came
       back, so the second opinion was dead for the page's whole life
       (measured: first request for this file aborted → refine() returned the
       base with no `deep` on every call; the guard stand lost scenarios A and
       D to exactly this while the dev server was dropping connections). */
    var _deep = null, _deepWorker = null, _deepReady = null, _deepId = 1, _deepPending = {};
    var DEEP_LO = 0.20, DEEP_HI = 0.55, DEEP_MUSIC_MIN = 0.10, DEEP_RATIO = 4, DEEP_SPAN_SEC = 6;
    /* ── v6 (2026-10-09): the second opinion also checks «solo vocal» ──────
       The hand vocal probe was calibrated on 2 sung takes and 4 speech files.
       Speech whose vowels a room, hiss, a phone line or a slow reader stretch
       past 0.6 s collects the same points a singer does (long voiced runs,
       pitched frames, a mid chroma spread) — with NO steady note at all
       (jitter and vibrato 0 in 14 of 15 cases). On /voice-enhancer that verdict
       picks the «Vocal» preset, whose noise stages are all OFF: 6 of 37 speech
       files of the engine bake-off (16 %) went out with their hiss untouched.
       Measured (node, this file + the shipped int8 YAMNet, same 3 × 6 s spans):
       263 speech files (FLEURS ru/en, TTS, our narration; clean and with hiss
       45/35 dB, phone 8 kHz μ-law, mp3 32k, rooms, noise, hum) → 23 called
       vocal here; YAMNet `singing` on ALL speech ≤ 0.0011. 288 sung takes
       (demucs vocal stems of 47 songs in ten languages incl. hip-hop, same
       degradations) → 214 called vocal; their `singing` p5 0.0038, median 0.04.
       The vocal verdict is dropped only when the model hears speech AND no
       singing: singing < 0.002 and speech ≥ 0.4 → 21 of the 23 fixed, 4 of
       the 214 lost (three are one track the model itself hears as speech,
       0.93). speech ≥ 0.2 would fix the last 2 (a TTS voice through a phone
       line / a room, speech 0.25) at the price of 2 more sung takes — the
       hand verdict stands unless the evidence is clear. A dropped vocal verdict
       also makes the content speech.
       Asked only by callers that use the subtype: refine(buf, base,
       {checkVocal: true}) — /voice-enhancer. */
    var VOCAL_SING_MAX = 0.002, VOCAL_SPEECH_MIN = 0.4;
    /* ── v10 (2026-10-29): …and the model's «singing» CONFIRMS a vocal too ──
       v6 let the model take a vocal verdict away and never give one. The hand
       probe misses a quarter of sung takes (74 of the 288 above: short notes,
       a speech-like run share), and every miss that lands on «speech» went out
       of /voice-enhancer through the SPEECH chain — the denoiser at full
       share, a gate on the note tails, a de-esser and a 3:1 compressor on a
       song (bug report 28.10, «ПЕСНЯ 77»: hand score 3, model singing 0.059;
       prod, 17 days: 52 such runs). The model's word is taken when it is
       clear and not a single passage: `singing` ≥ VOCAL_SING_MIN in at least
       two pieces of three (the whole buffer when it is one piece) and the
       pieces are not music (a voice over a band is a song, not a solo vocal)
       — then the subtype is «vocal», content as it was. Between this line and
       the v6 one (0.002 … 0.004) the hand verdict stands; 0.004 is 3.6× the
       highest speech file of the v6 calibration and its sung p5.
       Measured through refine() with the shipped model's answers per piece:
       sung takes routed to the speech chain 77 → 32 of 324 (288 demucs stems
       and 36 full a cappella tracks from ccMixter — those 9 → 0); 263 speech
       files, 161 songs with a band and 24 speech adversaries (long pauses, a
       sung jingle, a sung passage, speech over a song at −10/−15/−20 dB) — not
       one verdict changed. Taking the vocal verdict AWAY by pieces as well
       («speech in two pieces of three») was measured and dropped: it fixed 5
       synthetic talks with a sung insert and sent 16 sung takes back to the
       speech chain — the v6 rule on the means stays. */
    var VOCAL_SING_MIN = 0.004, VOCAL_PIECES_NEED = 2;
    /* ── v7 (2026-10-10): a CONFIDENT «music» verdict meets the model too ──
       Above the band (hand score ≥ 0.55) the hand verdict stood unasked. On
       real noisy speech that is where a printer, a copier or a kitchen under
       the voice lands — steady, rhythmic, tonal — and a speech tool answers
       «music» by switching its noise stages OFF: 3 of 59 recordings of the
       DNS Challenge test set went out of /voice-enhancer untouched (hand score
       0.6–0.7; the model on them: speech 0.67–0.90, music 0.001–0.016).
       Measured on the calibration of v6 plus 114 full songs: of 40 songs and 9
       sung takes the hand scored ≥ 0.55, the model's `music` is never below
       0.067 and its `speech` never above 0.121 — «music < 0.05 and speech ≥ 0.4
       → speech» overturns none of them and all 3 of the speech files.
       Asked only by callers for whom «music» means «do not denoise»:
       refine(buf, base, {checkMusic: true}). Others (tempo, pitch) see a song
       nine times out of ten and would load the model for nothing. */
    var MUSIC_DENY_MUSIC_MAX = 0.05, MUSIC_DENY_SPEECH_MIN = 0.4;
    /* ── v8 (2026-10-14): the model answers PER PIECE, and a confident «speech»
       meets it too ─────────────────────────────────────────────────────────
       Support chat 685: a 71-minute audience recording of a musical went out
       of /denoise through the «Voice» profile three times (highpass 120 Hz,
       60 dB of spectral subtraction). Two things were wrong, both measured on
       76 real long recordings from archive.org (33 live music — audience tapes,
       opera, a musical, orchestras, choirs; 38 speech — lectures, stand-up with
       an audience, sermons, conference talks, audiobooks; 3 recitations):

       1. The hand scorer is weak on LIVE music. Pieces of 12 s it called
          «speech» in 30 % of the live-music recordings — below the band, with
          a score of 0.10, where nobody asked the model (that musical, its
          piece at 75 %: hand 0.10, model music 0.42 / speech 0.06). The model
          on the same pieces: music ≥ 0.10 and ≥ 4 × speech on every music
          piece but the talk between songs, and music 0.00 on every speech
          piece — applause, laughter and a reverberant hall included.
          → `checkSpeech`: callers for whom «speech» lets a speech denoiser
          touch the file ask the model below the band as well.

       2. One answer for three pieces is the wrong shape. A musical is songs
          AND dialogue; its pieces read «speech 0.92», «music 0.49 / 0.10»,
          «music 0.42 / 0.06», the MEAN reads music 0.30 / speech 0.36, and the
          ratio rule fails on it. Judged piece by piece, with «music when at
          least two of three are» — 33 / 33 live music, 38 / 38 speech, 3 / 3
          recitation (the mean rule: 31 / 33; «at least one»: 2 speech files
          with a jingle flip; «all three»: 26 / 33). A buffer short enough to
          be one piece is one piece, as before.

       `deep.mix` = {music, speech, of} — how the pieces fell: a caller may
       tell the person the recording is both. Which pieces: whatever the
       buffer holds at its start, middle and end (deepPcm) — for a recording
       too long to decode that buffer is AudioRangeSource.spread(), 25 % /
       50 % / 75 % of the file, never its first seconds. */
    var SPAN_MUSIC_NEED = 2;
    /* Music ONLY when the model hears music AND hardly any speech: narration
       over a music bed is the case this must not flip — the guard corpus'
       jazz bed at −15 dB reads music 0.60 / speech 0.18 (ratio 3.3), its pop
       bed at −8 dB 0.68 / 0.50, while a rapped hip-hop song is the closest
       music at 0.67 / 0.12 (ratio 5.7) and every other song ≥9.8. The
       music-probability floor keeps the lullaby (0.155 / 0.000). */

    /* ── v9 (2026-10-29): listen where the sound is ───────────────────────
       Every probe here looked at fixed places — the first, the middle and the
       last seconds of the buffer — so that one quiet intro could not decide a
       file alone. A vocal track exported from a song (the voice by itself,
       silent wherever the instruments played) is silent exactly there. Bug
       report 28.10, «ПЕСНЯ 77»: 190 s, the voice from 31 s to 168 s; the hand
       probe heard the singing in one window of three (score 3, the verdict
       needs 4), the model in one span of three (singing 0.059 — a third of
       what the sung span read), and /voice-enhancer sent the song through its
       speech chain at full strength. A window that lands on silence now moves
       to the sound inside ITS third of the file: the spread stays, the silence
       goes. «Silence» is relative to the file — 30 dB under its loud level
       (p95 frame) — so a quiet take is not all silence and a noisy one is all
       sound. A window with sound in at least half of its frames stays where it
       was, which keeps a file without long pauses judged exactly as before.
       `listenMoved` in analyze() and `deep.moved` in refine() count the
       windows that moved. */
    var SOUND_FRAME = 2048, SOUND_REL_DB = 30, SOUND_KEEP = 0.5, SOUND_GAIN = 0.25;
    var _soundEnv = (typeof WeakMap === 'function') ? new WeakMap() : null;
    function soundEnvelope(samples, sr) {
        var hit = _soundEnv && _soundEnv.get(samples);
        if (hit && hit.sr === sr && hit.len === samples.length) return hit;
        var n = Math.floor(samples.length / SOUND_FRAME), rms = new Float32Array(n);
        for (var f = 0; f < n; f++) {
            var e = 0, o = f * SOUND_FRAME;
            for (var i = 0; i < SOUND_FRAME; i++) { var v = samples[o + i]; e += v * v; }
            rms[f] = Math.sqrt(e / SOUND_FRAME);
        }
        var sorted = Array.prototype.slice.call(rms).sort(function (a, b) { return a - b; });
        var thr = (sorted.length ? sorted[Math.floor(sorted.length * 0.95)] : 0) * Math.pow(10, -SOUND_REL_DB / 20);
        var pre = new Int32Array(n + 1);
        for (var k = 0; k < n; k++) pre[k + 1] = pre[k] + (rms[k] > 0 && rms[k] >= thr ? 1 : 0);
        var env = { sr: sr, len: samples.length, n: n, pre: pre };
        if (_soundEnv) _soundEnv.set(samples, env);
        return env;
    }
    function soundShare(env, s0, len) {
        var f0 = Math.floor(s0 / SOUND_FRAME), f1 = Math.min(env.n, Math.ceil((s0 + len) / SOUND_FRAME));
        return f1 > f0 ? (env.pre[f1] - env.pre[f0]) / (f1 - f0) : 0;
    }
    /** The probe's own window starts (samples), each moved onto sound inside
     *  its share of the buffer when it sat on silence → {starts, moved}. */
    function soundedStarts(samples, sr, starts, len) {
        var total = samples.length, k = starts.length;
        if (!k || len >= total) return { starts: starts, moved: 0 };
        var env = soundEnvelope(samples, sr), out = [], moved = 0;
        var step = Math.max(SOUND_FRAME, Math.floor(sr * 0.5));
        for (var i = 0; i < k; i++) {
            var s = starts[i], c = soundShare(env, s, len);
            if (c >= SOUND_KEEP) { out.push(s); continue; }
            var lo = Math.floor(total * i / k), hi = Math.floor(total * (i + 1) / k) - len;
            if (hi < lo) { lo = Math.max(0, Math.min(lo, total - len)); hi = lo; }
            var best = s, bestC = c;
            for (var p = lo; p <= hi; p += step) {
                var cp = soundShare(env, p, len);
                if (cp > bestC) { bestC = cp; best = p; }
            }
            if (best !== s && bestC >= c + SOUND_GAIN) { out.push(best); moved++; } else out.push(s);
        }
        return { starts: out, moved: moved };
    }
    function configureDeep(cfg) { _deep = cfg && cfg.workerUrl && cfg.ortUrl && cfg.modelUrl ? cfg : null; }

    /* The config at the moment of use: what configureDeep() set, otherwise
       whatever the page published — read now, not at DOMContentLoaded, so
       neither the order the scripts arrived in nor a late ORT build helper
       decides it. */
    function deepConfig() {
        if (!_deep && typeof window.__contentDetectDeep === 'function') {
            try { configureDeep(window.__contentDetectDeep()); } catch (_) {}
        }
        return _deep;
    }

    function deepSend(msg, transfer) {
        return new Promise(function (resolve, reject) {
            var id = _deepId++;
            _deepPending[id] = { resolve: resolve, reject: reject };
            msg.id = id;
            try { _deepWorker.postMessage(msg, transfer || []); } catch (e) { delete _deepPending[id]; reject(e); }
        });
    }

    function deepInit() {
        if (_deepReady) return _deepReady;
        _deepReady = new Promise(function (resolve, reject) {
            try {
                _deepWorker = new Worker(_deep.workerUrl);
            } catch (e) { reject(e); return; }
            _deepWorker.onmessage = function (ev) {
                var m = ev.data || {}, p = _deepPending[m.id];
                if (!p) return;
                delete _deepPending[m.id];
                if (m.type === 'error') p.reject(new Error(m.message || 'worker error')); else p.resolve(m);
            };
            _deepWorker.onerror = function (e) {
                var err = new Error((e && e.message) || 'worker error');
                Object.keys(_deepPending).forEach(function (k) { _deepPending[k].reject(err); delete _deepPending[k]; });
                reject(err);
            };
            deepSend({ type: 'init', ortUrl: _deep.ortUrl, wasmBase: _deep.wasmBase || null, modelUrl: _deep.modelUrl, threads: 1 })
                .then(resolve, reject);
        });
        _deepReady.catch(function () { try { _deepWorker && _deepWorker.terminate(); } catch (_) {} _deepWorker = null; _deepReady = null; });
        return _deepReady;
    }

    /* Bring the second opinion up BEFORE there is a file to ask about — when the
       person reaches for the upload zone. Cold, it is a worker, the 12 MB WASM
       runtime and a 3.9 MB model: prod, 21 days of /voice-enhancer, the answer
       took 2.7 s at the median, 9.9 s at p90 and more than 15 s for 5.5 % — and a
       third of the expected answers never came at all, so the hand verdict stood
       («music» or «vocal» over noisy speech = the noise stages off). The file
       picker is open for longer than that. Resolves true/false, never rejects;
       a page without the partial resolves false and loads nothing. */
    function warmDeep() {
        if (!deepConfig()) return Promise.resolve(false);
        return deepInit().then(function () { return true; }, function () { return false; });
    }

    /* 16 kHz mono, three spans of DEEP_SPAN_SEC (start / middle / end) like
       the hand scorer, so one quiet intro cannot decide a file — 18 s, ~18
       patches (measured enough: speech ≤0.017, songs ≥0.155 on the corpus).
       A span that lands on silence moves onto sound in its third (v9). */
    function deepPcm(buf) {
        var sr = buf.sampleRate, n = buf.length, span = Math.floor(DEEP_SPAN_SEC * sr), pieces = [], moved = 0;
        if (n <= span * 3) pieces.push([0, n]);
        else {
            var heard = soundedStarts(buf.getChannelData(0), sr, [0, Math.floor((n - span) / 2), n - span], span);
            moved = heard.moved;
            for (var h = 0; h < heard.starts.length; h++) pieces.push([heard.starts[h], heard.starts[h] + span]);
        }
        var chs = [];
        for (var c = 0; c < Math.min(2, buf.numberOfChannels); c++) chs.push(buf.getChannelData(c));
        var total = 0;
        for (var i = 0; i < pieces.length; i++) total += pieces[i][1] - pieces[i][0];
        var mono = new Float32Array(total), o = 0;
        for (var j = 0; j < pieces.length; j++) {
            for (var k = pieces[j][0]; k < pieces[j][1]; k++, o++) mono[o] = chs.length === 2 ? (chs[0][k] + chs[1][k]) * 0.5 : chs[0][k];
        }
        /* The lengths of the pieces at 16 kHz, so the worker can judge each
           one alone; the last takes what rounding left. */
        function spansOf(n16) {
            if (pieces.length < 2) return null;
            var out = [], used = 0;
            for (var q = 0; q < pieces.length - 1; q++) { var l = Math.round((pieces[q][1] - pieces[q][0]) * 16000 / sr); out.push(l); used += l; }
            out.push(Math.max(1, n16 - used));
            return out;
        }
        if (sr === 16000) return Promise.resolve({ pcm: mono, spans: spansOf(mono.length), moved: moved });
        var len = Math.ceil(mono.length * 16000 / sr);
        var ctx = new OfflineAudioContext(1, len, 16000);
        var src = ctx.createBufferSource(), b = ctx.createBuffer(1, mono.length, sr);
        b.copyToChannel(mono, 0); src.buffer = b; src.connect(ctx.destination); src.start();
        return ctx.startRendering().then(function (r) { var d = r.getChannelData(0); return { pcm: d, spans: spansOf(d.length), moved: moved }; });
    }

    /* One piece is music when the model hears music and hardly any speech —
       the same two numbers that decided the mean (DEEP_MUSIC_MIN, DEEP_RATIO). */
    function pieceIsMusic(x) { return x.music >= DEEP_MUSIC_MIN && x.music >= DEEP_RATIO * Math.max(x.speech, 0.001); }
    /** How the pieces fell: {music, speech, of, isMusic}. A worker that does
     *  not answer per piece (an old cached copy) gives one piece — the mean. */
    function pieceVote(r) {
        var list = (r.spans && r.spans.length) ? r.spans : [{ speech: r.speech, music: r.music }];
        var music = 0, speech = 0;
        for (var i = 0; i < list.length; i++) {
            if (pieceIsMusic(list[i])) music++;
            else if (list[i].speech >= MUSIC_DENY_SPEECH_MIN) speech++;
        }
        return { music: music, speech: speech, of: list.length, isMusic: music >= Math.min(SPAN_MUSIC_NEED, list.length) };
    }
    /** In how many pieces the model hears singing (v10): {sung, of, need}. A
     *  worker that does not answer per piece gives one piece — the mean; one
     *  without `singing` gives none. */
    function singingVote(r) {
        var list = (r.spans && r.spans.length && typeof r.spans[0].singing === 'number') ? r.spans : [{ singing: r.singing }];
        var sung = 0;
        for (var i = 0; i < list.length; i++) if (typeof list[i].singing === 'number' && list[i].singing >= VOCAL_SING_MIN) sung++;
        return { sung: sung, of: list.length, need: Math.min(VOCAL_PIECES_NEED, list.length) };
    }

    function refine(audioBuffer, base, opts) {
        var result = base || analyze(audioBuffer);
        if (!deepConfig() || !result || result.error || typeof result.musicScore !== 'number') return Promise.resolve(result);
        var s = result.musicScore;
        var inBand = s >= DEEP_LO && s < DEEP_HI;
        var askVocal = !!(opts && opts.checkVocal) && result.subtype === 'vocal';
        var askSinging = !!(opts && opts.checkVocal) && result.subtype !== 'vocal';
        var askMusic = !!(opts && opts.checkMusic) && result.content === 'music' && result.subtype !== 'vocal' && s >= DEEP_HI;
        var askSpeech = !!(opts && opts.checkSpeech) && result.content === 'speech' && s < DEEP_LO;
        if (!inBand && !askVocal && !askMusic && !askSpeech && !askSinging) return Promise.resolve(result);
        var timeoutMs = (opts && opts.timeoutMs) || 30000;
        var t0 = Date.now(), moved = 0;
        var work = deepInit()
            .then(function () { return deepPcm(audioBuffer); })
            .then(function (d) { moved = d.moved; return deepSend({ type: 'classify', pcm: d.pcm, spans: d.spans }, [d.pcm.buffer]); })
            .then(function (r) {
                var vote = pieceVote(r), sv = singingVote(r);
                var deep = { speech: Math.round(r.speech * 1000) / 1000, music: Math.round(r.music * 1000) / 1000,
                             singing: typeof r.singing === 'number' ? Math.round(r.singing * 10000) / 10000 : null,
                             patches: r.patches, ms: Date.now() - t0, moved: moved,
                             mix: { music: vote.music, speech: vote.speech, of: vote.of },
                             sung: sv.sung,
                             // applause / cheering / crowd in the pieces heard (yamnet-classify-worker.js)
                             audience: r.audience ? { peak: Math.round(r.audience.peak * 1000) / 1000, heard: r.audience.heard | 0 } : null };
                var out = Object.assign({}, result, { deep: deep, handContent: result.content, handSubtype: result.subtype });
                if (inBand) out.content = vote.isMusic ? 'music' : 'speech';
                if (askSpeech && vote.isMusic) out.content = 'music';
                if (askMusic && deep.music < MUSIC_DENY_MUSIC_MAX && deep.speech >= MUSIC_DENY_SPEECH_MIN) out.content = 'speech';
                if (askVocal && deep.singing !== null && deep.singing < VOCAL_SING_MAX && deep.speech >= VOCAL_SPEECH_MIN) {
                    /* The model hears a voice that speaks and does not sing: that is
                       speech, whatever the hand content said underneath — a phone-line
                       recording (8 kHz) scored «music» by hand once «vocal» was lifted
                       went to the music preset, noise stages off again. */
                    out.subtype = null;
                    out.content = 'speech';
                }
                /* v10: the voice the model hears singing in most of the pieces, with
                   no band under it, is a sung vocal whatever the hand probe made of
                   it — the content (speech / music) stays as decided above. */
                if (askSinging && sv.sung >= sv.need && !vote.isMusic) out.subtype = 'vocal';
                return out;
            });
        var timer = new Promise(function (resolve) { setTimeout(function () { resolve(Object.assign({}, result, { deep: { timeout: 1, ms: Date.now() - t0 } })); }, timeoutMs); });
        return Promise.race([work, timer]).catch(function (e) {
            return Object.assign({}, result, { deep: { error: String((e && e.message) || e).slice(0, 120), ms: Date.now() - t0 } });
        });
    }

    function analyze(audioBuffer) {
        try {
            var sr = audioBuffer.sampleRate;
            var samples = audioBuffer.getChannelData(0);
            var fftSize = 2048;
            var binHz = sr / fftSize;
            var lowBins = Math.floor(4000 / binHz);
            var highBins = Math.floor(8000 / binHz);
            var totalBins = fftSize / 2;

            var dur = samples.length / sr;
            var winDur = 5;
            var winStarts = [0];
            if (dur > 12) winStarts.push(Math.floor(((dur - winDur) / 2) * sr));
            if (dur > 22) winStarts.push(Math.floor((dur - winDur) * sr));
            var listen = soundedStarts(samples, sr, winStarts, Math.floor(winDur * sr));
            winStarts = listen.starts;

            var gLowEnergy = 0, gHighEnergy = 0, gTotalEnergy = 0;
            var gSilent = 0, gFrames = 0;
            var transientRatios = [];

            for (var w = 0; w < winStarts.length; w++) {
                var start = winStarts[w];
                var end = Math.min(samples.length, start + Math.floor(winDur * sr));
                var lowEnergy = 0, highEnergy = 0, totalEnergy = 0;
                var silent = 0, frames = 0, winTransients = 0;
                var emaRms = 0, prevRms = 0;

                var hop = Math.floor(fftSize / 2);
                var processedFrames = 0;
                var maxFramesPerWindow = 60;
                var binStride = 4;
                for (var offset = start; offset + fftSize <= end && processedFrames < maxFramesPerWindow; offset += hop) {
                    processedFrames++;
                    frames++;
                    var rms = 0;
                    for (var i = 0; i < fftSize; i++) rms += samples[offset + i] * samples[offset + i];
                    rms = Math.sqrt(rms / fftSize);
                    if (rms < 0.005) { silent++; continue; }
                    emaRms = emaRms === 0 ? rms : (0.85 * emaRms + 0.15 * rms);
                    if (rms > 3 * emaRms && rms > prevRms) winTransients++;
                    prevRms = rms;

                    // Goertzel per stride-4 bin. NOTE: the inner angular-freq var
                    // MUST NOT be named `w` — that would shadow the outer window
                    // counter and bail the window loop after window 1 (the
                    // calibration-breaking shadow bug, audio-enhancer:2568).
                    for (var k = 0; k < totalBins; k += binStride) {
                        var gw = 2 * Math.PI * k / fftSize;
                        var cw = 2 * Math.cos(gw);
                        var sPrev = 0, sPrev2 = 0;
                        for (var n = 0; n < fftSize; n++) {
                            var s = samples[offset + n] + cw * sPrev - sPrev2;
                            sPrev2 = sPrev;
                            sPrev = s;
                        }
                        var mag = sPrev * sPrev + sPrev2 * sPrev2 - cw * sPrev * sPrev2;
                        if (mag < 0) mag = 0;
                        totalEnergy += mag;
                        if (k < lowBins) lowEnergy += mag;
                        if (k >= highBins) highEnergy += mag;
                    }
                }

                gLowEnergy += lowEnergy; gHighEnergy += highEnergy;
                gTotalEnergy += totalEnergy; gSilent += silent; gFrames += frames;
                transientRatios.push(winTransients / Math.max(1, frames - silent));
            }

            // Whole-file noise floor (time domain, dBFS) — non-overlapping ~46 ms
            // RMS, p05 = floor, p70 = content reference (not median: pause-heavy
            // speech is ~50 % floor frames and a median would veto exactly the
            // files with the clearest floor). Trustworthy only when p70−p05 ≥ 8 dB
            // (wall-of-sound music sits on content → stays null).
            var noiseFloorDb = null, noiseFloorRefDb = null;
            var nfRms = [];
            for (var fo = 0; fo + fftSize <= samples.length; fo += fftSize) {
                var fr = 0;
                for (var fi = 0; fi < fftSize; fi++) fr += samples[fo + fi] * samples[fo + fi];
                nfRms.push(Math.sqrt(fr / fftSize));
            }
            // relSilentRatio — the pause measure that replaced the absolute one.
            // The legacy `silentRatio` counted frames under a FIXED -46 dBFS
            // (rms < 0.005). Room tone sits above that line, so a voice memo
            // recorded next to a fridge has ZERO "silent" frames and scored as
            // music by that feature alone — measured 1/40 correct on noisy
            // speech, 2026-08-19. This one asks the noise-independent question:
            // how much of the file sits ≥15 dB below the file's OWN loud level.
            // Free: reuses the frame-RMS pass already walked for the floor.
            var relSilentRatio = null;
            if (nfRms.length >= 40) {
                nfRms.sort(function (a, b) { return a - b; });
                var floorDb = 20 * Math.log10(Math.max(1e-7, nfRms[Math.floor(nfRms.length * 0.05)]));
                var refDb = 20 * Math.log10(Math.max(1e-7, nfRms[Math.floor(nfRms.length * 0.70)]));
                noiseFloorRefDb = Math.round(refDb * 10) / 10;
                if (refDb - floorDb >= 8) noiseFloorDb = Math.round(floorDb * 10) / 10;
                var loudRms = nfRms[Math.floor(nfRms.length * 0.95)];
                var quietThr = loudRms * 0.1778;   // -15 dB relative to p95
                var quietN = 0;
                for (var qi = 0; qi < nfRms.length && nfRms[qi] < quietThr; qi++) quietN++;
                relSilentRatio = quietN / nfRms.length;
            }

            // Stereo decorrelation — REPORTED, no longer SCORED (2026-08-19).
            // The booster rested on "genuinely-stereo content is almost never
            // speech". Measured on stereo speech recorded in a room: the two
            // channels pick up DECORRELATED room noise, so stereoDiff reads
            // 0.15-0.999 on plain noisy speech — the same range as music. Even
            // measured honestly (correlation over the loudest frames only) the
            // two classes overlap completely, so this carries no weight now.
            // Kept in the payload: it is the input to that finding and to the
            // dual-mono checks downstream. Correlation over a mid 4 s slice.
            var stereoDiff = null;
            if (audioBuffer.numberOfChannels >= 2) {
                var L = audioBuffer.getChannelData(0);
                var R = audioBuffer.getChannelData(1);
                var n0 = Math.floor(L.length * 0.25);
                var n1 = Math.min(L.length, n0 + sr * 4);
                var sLL = 0, sRR = 0, sLR = 0;
                for (var si = n0; si < n1; si++) {
                    var lv = L[si], rv = R[si];
                    sLL += lv * lv; sRR += rv * rv; sLR += lv * rv;
                }
                var denom = Math.sqrt(sLL * sRR);
                var corr = denom > 1e-12 ? (sLR / denom) : 1;
                stereoDiff = Math.round((1 - Math.min(1, Math.abs(corr))) * 1000) / 1000;
            }

            if (gTotalEnergy === 0) {
                return { content: 'speech', musicScore: 0, silentRatio: 1, highRatio: 0,
                    lowBandRatio: 1, transientRatio: 0, stereoDiff: stereoDiff,
                    noiseFloorDb: noiseFloorDb, noiseFloorRefDb: noiseFloorRefDb, durationS: dur,
                    beatAcf: null, bpm: null, relSilentRatio: relSilentRatio, subtype: null, vocal: null };
            }

            var silentRatio = gSilent / Math.max(1, gFrames);
            var highRatio = gHighEnergy / Math.max(1, gTotalEnergy);
            var lowBandRatio = gLowEnergy / Math.max(1e-12, gTotalEnergy);
            var avgTransientRatio = 0;
            for (var ti = 0; ti < transientRatios.length; ti++) avgTransientRatio += transientRatios[ti];
            avgTransientRatio = avgTransientRatio / Math.max(1, transientRatios.length);

            // Rhythm + sustained-pitch evidence. Both are properties MUSIC has
            // and SPEECH does not, and neither is manufactured by background
            // noise — which is the whole point of the 2026-08-19 rewrite.
            var rh = rhythmProbe(samples, sr);
            var vf = null;
            try { if (dur >= 3) vf = vocalProbe(samples, sr); } catch (_vp) { vf = null; }
            var runShare = vf ? vf.voicedRunShare : null;

            /* Scorer v3 (2026-08-19) — measured, not guessed. Calibrated on a
               107-file labeled corpus (37 real songs from public/audio/ai-styles
               + a real mix, 16 clean-speech recordings, 53 noisy-speech mixes
               built from tracked household sounds at +12/+6/0 dB SNR, mono and
               stereo, incl. a narrowband phone case). Guard:
               tests/playwright/content-detect-corpus.mjs.

               v2 scored 43.5 % on that corpus — WORSE than always answering
               "speech" — because it was carried by `silentRatio`, a feature that
               background noise erases. Noisy speech: 1/40 correct. That verdict
               then switched /voice-enhancer's denoiser OFF, so the noisier the
               recording, the more certainly the tool refused to clean it.

               v3: 96.3 % — music recall 34/38, and ZERO of the 69 speech files
               (clean or noisy) read as music.
                 · beatAcf    — rhythmic self-similarity of the onset envelope.
                                Music median 0.51 · speech 0.20 · noisy speech 0.07.
                 · runShare   — share of long sustained voiced runs (free: the
                                vocal probe already computes it). Music 0.67 ·
                                speech 0.22 · noisy speech 0.18.
                 · relSilent  — pauses relative to the file's own level (above).
                 · highRatio / lowBandRatio — unchanged weak boosters.
               Dropped: absolute silentRatio (noise erases it), transientRatio
               (measured 0 on every class), the stereo booster (see above). */
            var musicScore = 0;
            // 0.40 and not 0.45: a beat ALONE must not reach the 0.45 verdict
            // line. Evenly-spaced speech-like bursts (a test generator, a
            // metronome, a stutter) autocorrelate like a drum machine — the
            // music-suite's synthetic speech fixture reads beatAcf 0.704 — so
            // the beat needs one corroborating signal (sustained pitch, filled
            // pauses, spectral balance) before it decides. Costs exactly one
            // real track on the corpus (a mono lo-fi downmix); buys immunity to
            // the whole periodic-speech family.
            if (rh.beatAcf >= 0.40) musicScore += 0.40;
            else if (rh.beatAcf >= 0.26) musicScore += 0.20;
            else if (rh.beatAcf >= 0.18) musicScore += 0.10;
            if (runShare !== null) {
                if (runShare >= 0.55) musicScore += 0.20;
                else if (runShare >= 0.30) musicScore += 0.10;
            }
            // v4: the loud sound sits on the semitone grid → an instrument (or
            // singing) is playing. This is the evidence beatless music has and
            // speech cannot manufacture: a rubato solo piano has no beat
            // (beatAcf 0.19) and scored 0.30 → "speech" → the speech denoiser
            // cut 31 % of its audible frames to digital silence (support
            // 2026-09-12). Thresholds sit 0.06 above the speech ceiling
            // (0.64 across 51 speech files incl. tone/hum/music-bed adversaries)
            // and 0.03 below the real piano (0.73); the corpus guard holds both.
            // ≥0.80 is decisive on its own (0.45): a staccato solo — short
            // notes, no sustained runs, pauses like speech — has NOTHING else
            // to score on, yet sits 0.16 above the speech ceiling.
            // The third tier is the real-recording tier: the support piano
            // measures 0.69 on channel 0 in the browser (0.73 on a mono
            // downmix) — 0.05 above the speech ceiling, too close for the
            // grid alone, so it must ALSO hold ≥6 pitch-stable runs of
            // ≥300 ms (piano 9; speech ≤1; a tone under speech ≤4 — one pitch
            // class, however long it lasts).
            if (vf && vf.gridFrames >= 40) {
                if (vf.gridShare >= 0.80) musicScore += 0.45;
                else if (vf.gridShare >= 0.70) musicScore += 0.30;
                else if (vf.gridShare >= 0.66 && vf.stableRuns >= 6) musicScore += 0.20;
            }
            if (relSilentRatio !== null) {
                if (relSilentRatio < 0.10) musicScore += 0.20;
                else if (relSilentRatio < 0.20) musicScore += 0.10;
            } else if (silentRatio < 0.10) {
                // File too short for the frame-RMS pass (<2 s): fall back to the
                // legacy absolute measure rather than scoring blind.
                musicScore += 0.20;
            }
            if (highRatio > 0.01) musicScore += 0.10;
            if (lowBandRatio < 0.90) musicScore += 0.10;

            var isMusic = musicScore >= 0.45;
            var result = {
                content: isMusic ? 'music' : 'speech',
                musicScore: Math.round(musicScore * 100) / 100,
                silentRatio: Math.round(silentRatio * 100) / 100,
                highRatio: Math.round(highRatio * 1000) / 1000,
                lowBandRatio: Math.round(lowBandRatio * 100) / 100,
                transientRatio: Math.round(avgTransientRatio * 1000) / 1000,
                stereoDiff: stereoDiff,
                noiseFloorDb: noiseFloorDb,
                noiseFloorRefDb: noiseFloorRefDb,
                durationS: Math.round(dur * 10) / 10,
                // v3 features (the ones that now carry the verdict)
                beatAcf: rh.beatAcf,
                bpm: rh.bpm,
                relSilentRatio: relSilentRatio === null ? null : Math.round(relSilentRatio * 100) / 100,
                listenMoved: listen.moved,
                subtype: null,
                vocal: null
            };
            // v2 (2026-07-16): solo-singing probe — additive, never changes
            // `content`. Consumers opt in via result.subtype === 'vocal'.
            // vf was computed above (the scorer needs voicedRunShare); reuse it
            // rather than paying for a second pitch pass.
            try {
                if (vf) {
                    result.vocal = vf;
                    if (isVocalVerdict(vf)) result.subtype = 'vocal';
                }
            } catch (_ve) { /* probe failure = no subtype, base contract intact */ }
            return result;
        } catch (e) {
            // Any failure → safe default 'speech' (the historical behavior).
            return { content: 'speech', musicScore: 0, silentRatio: null, highRatio: null,
                lowBandRatio: null, transientRatio: null, stereoDiff: null,
                noiseFloorDb: null, noiseFloorRefDb: null, durationS: 0, subtype: null, vocal: null,
                beatAcf: null, bpm: null, relSilentRatio: null,
                error: String(e && e.message || e) };
        }
    }

    /* ── v3: rhythm probe ───────────────────────────────────────────────
       Music repeats itself on a beat; speech does not. The onset envelope
       (half-wave-rectified frame-energy rise) is autocorrelated over lags of
       0.30-1.30 s (46-200 BPM) and the normalized peak is the score.

       Why this feature and not a louder version of the old ones: background
       noise ADDS energy but adds no periodicity, so beatAcf is the one music
       marker a noisy room cannot manufacture. Measured on the calibration
       corpus — music 0.51 median, clean speech 0.20, noisy speech 0.07 (noisy
       speech scores BELOW clean speech: a steady noise bed flattens the
       envelope). Contrast with silentRatio, which noise drives to 0 and thereby
       votes "music" exactly when the user needs denoising most.

       Budget: ≤3 windows × 10 s, hop 512 (~86 fps), ~110 lags → a few ms.
       Per-window peaks are combined with a MEDIAN, so one quiet intro or one
       applause burst cannot decide the file. */
    function rhythmProbe(samples, sr) {
        var WIN = 1024, HOP = 512;
        var fps = sr / HOP;
        var dur = samples.length / sr;
        var winDur = Math.min(10, dur);
        var starts = [0];
        if (dur > 24) starts.push(Math.floor(((dur - winDur) / 2) * sr));
        if (dur > 44) starts.push(Math.floor((dur - winDur) * sr));
        starts = soundedStarts(samples, sr, starts, Math.floor(winDur * sr)).starts;
        var loLag = Math.round(fps * 0.30), hiLag = Math.round(fps * 1.30);
        var peaks = [], lags = [];

        for (var w = 0; w < starts.length; w++) {
            var s0 = starts[w];
            var end = Math.min(samples.length - WIN, s0 + Math.floor(winDur * sr));
            var env = [];
            for (var off = s0; off < end; off += HOP) {
                var e = 0;
                for (var i = 0; i < WIN; i++) { var v = samples[off + i]; e += v * v; }
                env.push(Math.sqrt(e / WIN));
            }
            if (env.length < hiLag * 2) continue;
            // Half-wave-rectified first difference = onset strength, then mean-removed
            // so the autocorrelation measures periodicity and not DC level.
            var flux = [], mean = 0;
            for (var k = 1; k < env.length; k++) { var d = env[k] - env[k - 1]; var f = d > 0 ? d : 0; flux.push(f); mean += f; }
            mean /= Math.max(1, flux.length);
            for (var k2 = 0; k2 < flux.length; k2++) flux[k2] -= mean;

            var best = 0, bestLag = 0;
            for (var L = loLag; L <= hiLag; L++) {
                var num = 0, a = 0, b = 0;
                for (var i2 = 0; i2 + L < flux.length; i2++) {
                    var x = flux[i2], y = flux[i2 + L];
                    num += x * y; a += x * x; b += y * y;
                }
                var den = Math.sqrt(a * b);
                var c = den > 1e-12 ? num / den : 0;
                if (c > best) { best = c; bestLag = L; }
            }
            peaks.push(best); lags.push(bestLag);
        }
        if (!peaks.length) return { beatAcf: 0, bpm: 0 };
        var sortedP = peaks.slice().sort(function (a, b) { return a - b; });
        var med = sortedP[Math.floor(sortedP.length / 2)];
        var mi = peaks.indexOf(med);
        return {
            beatAcf: Math.round(med * 1000) / 1000,
            bpm: lags[mi] ? Math.round(60 * fps / lags[mi]) : 0
        };
    }

    /* ── v2: solo-singing (vocal) probe ─────────────────────────────────
       Calibrated 2026-07-16 on a real-file matrix (2 real vocals, 4 speech,
       5 music/instrumental, 2 synthetic adversaries — tests/scratch/ve-etalon).
       Design lessons baked in:
       · NCC octave/harmonic flips shred sung notes → ALL pitch statistics fold
         F0 to a pitch class (cents mod 1200); octave errors collapse.
       · voiced-run length is the F0-accuracy-independent separator: singing
         sustains phonation 0.3–1.2 s, speech breaks every 50–300 ms.
       · pitchedRatio separates solo voice (≥0.6) from full mixes (~0.3 —
         accompaniment masks periodicity).
       · machine kills: >0.7 chroma concentration (synth pitch grid), dead-flat
         long notes (jitter <0.1c), dead-smooth pure tone (low harmonics AND no
         jitter AND no vibrato).
       Budget: ≤3×5 s windows, hop 1024, coarse-to-fine NCC (stride 4 → ±5
       refine) — ~150-250 ms per file on desktop. */
    function vocalProbe(samples, sr) {
        var WIN = 2048, HOP = 1024;
        var dur = samples.length / sr;
        var winDur = 5;
        var starts = [0];
        if (dur > 12) starts.push(Math.floor(((dur - winDur) / 2) * sr));
        if (dur > 22) starts.push(Math.floor((dur - winDur) * sr));
        starts = soundedStarts(samples, sr, starts, Math.floor(winDur * sr)).starts;

        var track = [];   // folded cents 0..1200 | -1 silent | -2 unvoiced | -3 boundary
        var trackE = [];  // frame energy (rms²) parallel to `track` — weights the grid share (v4)
        var active = 0, voiced = 0;
        var hfE = 0, totE = 0, harmSum = 0, harmN = 0;
        var minLag = Math.floor(sr / 800), maxLag = Math.min(Math.floor(sr / 70), WIN - 1);

        for (var w = 0; w < starts.length; w++) {
            var s0 = starts[w];
            var end = Math.min(samples.length - WIN, s0 + winDur * sr);
            for (var off = s0; off < end; off += HOP) {
                var rms = 0;
                for (var i = 0; i < WIN; i++) rms += samples[off + i] * samples[off + i];
                rms = Math.sqrt(rms / WIN);
                if (rms < 0.004) { track.push(-1); trackE.push(0); continue; }
                active++;
                var best = 0, bestLag = 0;
                var lag, cVal;
                for (lag = minLag; lag <= maxLag; lag += 4) {
                    cVal = nccAt(samples, off, WIN, lag);
                    if (cVal > best) { best = cVal; bestLag = lag; }
                }
                var lo = Math.max(minLag, bestLag - 5), hi = Math.min(maxLag, bestLag + 5);
                var corrs = {};
                for (lag = lo; lag <= hi; lag++) {
                    cVal = corrs[lag] = nccAt(samples, off, WIN, lag);
                    if (cVal > best) { best = cVal; bestLag = lag; }
                }
                if (corrs[bestLag - 1] === undefined) corrs[bestLag - 1] = nccAt(samples, off, WIN, Math.max(1, bestLag - 1));
                if (corrs[bestLag + 1] === undefined) corrs[bestLag + 1] = nccAt(samples, off, WIN, bestLag + 1);

                if (best > 0.52 && bestLag > minLag && bestLag < maxLag) {
                    voiced++;
                    var ci = corrs[bestLag], cl = corrs[bestLag - 1], cr = corrs[bestLag + 1];
                    var dd = (cl - 2 * ci + cr);
                    var frac = Math.abs(dd) > 1e-9 ? 0.5 * (cl - cr) / dd : 0;
                    if (frac > 0.5) frac = 0.5; if (frac < -0.5) frac = -0.5;
                    var f0 = sr / (bestLag + frac);
                    var cents = 1200 * (Math.log(f0 / 55) / Math.LN2);
                    track.push(((cents % 1200) + 1200) % 1200); trackE.push(rms * rms);

                    var hf = bandE(samples, off, WIN, sr, 7000) + bandE(samples, off, WIN, sr, 9000) + bandE(samples, off, WIN, sr, 11000);
                    var all = hf + bandE(samples, off, WIN, sr, 500) + bandE(samples, off, WIN, sr, 1200) + bandE(samples, off, WIN, sr, 2500) + bandE(samples, off, WIN, sr, 3400);
                    hfE += hf; totE += all;
                    if (f0 < 1200) {
                        var e1h = bandE(samples, off, WIN, sr, f0), e2h = bandE(samples, off, WIN, sr, f0 * 2), e3h = bandE(samples, off, WIN, sr, f0 * 3);
                        var eS = e1h + e2h + e3h;
                        if (eS > 1e-12) { harmSum += (e2h + e3h) / eS; harmN++; }
                    }
                } else {
                    track.push(-2); trackE.push(0);
                }
            }
            track.push(-3); trackE.push(0);
        }

        var pitchedRatio = active ? voiced / active : 0;
        var frameMs = HOP / sr * 1000;

        // Chroma histogram (60×20c, tri-smoothed): top-4 concentration
        var BINS = 60;
        var hist = new Array(BINS); for (var hz = 0; hz < BINS; hz++) hist[hz] = 0;
        for (var t = 0; t < track.length; t++) {
            if (track[t] >= 0) hist[Math.min(BINS - 1, Math.floor(track[t] / 20))]++;
        }
        var sm = new Array(BINS), mass = 0;
        for (var bI = 0; bI < BINS; bI++) {
            sm[bI] = hist[bI] * 0.5 + hist[(bI + BINS - 1) % BINS] * 0.25 + hist[(bI + 1) % BINS] * 0.25;
            mass += sm[bI];
        }
        var order = [];
        for (var oI = 0; oI < BINS; oI++) order.push(oI);
        order.sort(function (x, y) { return sm[y] - sm[x]; });
        var top4 = 0;
        for (var o4 = 0; o4 < 4; o4++) top4 += sm[order[o4]];
        var chromaConc = mass > 0 ? top4 / mass : 0;

        // Folded-stable runs (circular ±100c, ≤2-frame flicker) → vibrato/jitter
        var minRun = Math.round(300 / frameMs);
        var runs = [];
        var cur = [], gap = 0;
        function cdist(a, b) { var d = Math.abs(a - b) % 1200; return d > 600 ? 1200 - d : d; }
        function flushRun() { if (cur.length >= minRun) runs.push(cur); cur = []; gap = 0; }
        for (var rI = 0; rI < track.length; rI++) {
            var fv = track[rI];
            if (fv < 0) {
                if (fv === -2 && cur.length && gap < 2) { gap++; continue; }
                flushRun(); continue;
            }
            if (!cur.length) { cur = [fv]; gap = 0; continue; }
            if (cdist(fv, cur[Math.floor(cur.length / 2)]) <= 100) { cur.push(fv); gap = 0; }
            else { flushRun(); cur = [fv]; }
        }
        flushRun();

        // Voiced-run continuity (F0-accuracy independent)
        var vRuns = [], vc = 0, vgap = 0;
        for (var vI = 0; vI < track.length; vI++) {
            if (track[vI] >= 0) { vc++; vgap = 0; }
            else if (vc && track[vI] === -2 && vgap < 2) { vgap++; }
            else { if (vc) vRuns.push(vc); vc = 0; vgap = 0; }
        }
        if (vc) vRuns.push(vc);
        var minLong = Math.round(600 / frameMs);
        var longFrames = 0;
        for (var lI = 0; lI < vRuns.length; lI++) if (vRuns[lI] >= minLong) longFrames += vRuns[lI];
        var voicedRunShare = voiced ? Math.min(1, longFrames / voiced) : 0;

        var vibHz = 0, vibDepth = 0, jitters = [];
        var frameRate = 1000 / frameMs;
        var stableLongRuns = 0;
        for (var q = 0; q < runs.length; q++) {
            var run = runs[q];
            if (run.length < minLong) continue;
            stableLongRuns++;
            var anchor2 = run[Math.floor(run.length / 2)];
            var cser = [];
            for (var cQ = 0; cQ < run.length; cQ++) {
                var d2 = run[cQ] - anchor2;
                if (d2 > 600) d2 -= 1200; if (d2 < -600) d2 += 1200;
                cser.push(d2);
            }
            var sd = [];
            for (var u = 1; u < cser.length - 1; u++) sd.push(Math.abs(cser[u] - (cser[u - 1] + cser[u + 1]) / 2));
            sd.sort(function (a, b) { return a - b; });
            if (sd.length) jitters.push(sd[Math.floor(sd.length / 2)]);
            for (var fvz = 3; fvz <= 9; fvz += 0.5) {
                var wv = 2 * Math.PI * fvz / frameRate, cv = 2 * Math.cos(wv), a1 = 0, a2 = 0;
                for (var m2 = 0; m2 < cser.length; m2++) { var vv = cser[m2] + cv * a1 - a2; a2 = a1; a1 = vv; }
                var mag = Math.sqrt(Math.max(0, a1 * a1 + a2 * a2 - cv * a1 * a2)) * 2 / cser.length;
                if (mag > vibDepth) { vibDepth = mag; vibHz = fvz; }
            }
        }
        jitters.sort(function (a, b) { return a - b; });
        var jitterCents = jitters.length ? jitters[Math.floor(jitters.length / 2)] : 0;

        /* ── v4 (2026-09-12): semitone-grid share, ENERGY-weighted ────────
           An instrument plays discrete pitches; speech glides. So the share of
           pitched frames whose folded F0 sits within ±25 c of a semitone (best
           tuning offset over 0..95 c) is ~0.5 for speech (a coin toss) and
           →1 for an instrument. Weighting frames by energy is what makes it
           safe: a sustained beep or a fridge hum UNDER speech also sits on the
           grid, but only in the pauses, at low energy — unweighted it reached
           0.92 on speech + a −10 dB tone; weighted it stays ≤0.64.
           Measured (node, 48 kHz mono): clean speech ≤0.63 (11), noisy speech
           ≤0.64 (29), speech + tone/hum/music-bed adversaries ≤0.64 (11),
           a real iPhone solo-piano recording 0.73 (the support case this
           feature exists for), the beatless orchestral track 0.79, sampled
           solo instruments ≥0.84 (87: piano, violin, cello, flute, oboe,
           trumpet, clarinet, harp, harpsichord, strings — legato, staccato,
           chords, clean and with a room bed at 20/10 dB SNR). Singing sits on
           the grid too (0.70–0.78), so this is music evidence, not an
           instrument-vs-voice separator. */
        var gridOff = 0, gridBestW = -1, gridWAll = 0;
        for (var gt = 0; gt < track.length; gt++) if (track[gt] >= 0) gridWAll += trackE[gt];
        if (gridWAll > 0) {
            for (var go = 0; go < 100; go += 5) {
                var wOn = 0;
                for (var gj = 0; gj < track.length; gj++) {
                    var gv = track[gj];
                    if (gv < 0) continue;
                    var gm = ((gv - go) % 100 + 100) % 100;
                    if (gm <= 25 || gm >= 75) wOn += trackE[gj];
                }
                if (wOn > gridBestW) { gridBestW = wOn; gridOff = go; }
            }
        }
        var gridShare = gridWAll > 0 ? gridBestW / gridWAll : 0;

        var f = {
            gridShare: Math.round(gridShare * 100) / 100,
            gridFrames: voiced,
            stableRuns: runs.length,
            pitchedRatio: Math.round(pitchedRatio * 100) / 100,
            chromaConc: Math.round(chromaConc * 100) / 100,
            voicedRunShare: Math.round(voicedRunShare * 100) / 100,
            stableLongRuns: stableLongRuns,
            vibratoHz: Math.round(vibHz * 10) / 10,
            vibratoDepthCents: Math.round(vibDepth * 10) / 10,
            jitterCents: Math.round(jitterCents * 100) / 100,
            hfNoiseRatio: totE > 0 ? Math.round(hfE / totE * 1000) / 1000 : 0,
            harmonicRichness: harmN ? Math.round(harmSum / harmN * 100) / 100 : 0
        };
        f.score = scoreVocal(f);
        return f;
    }

    function nccAt(samples, off, WIN, lag) {
        var ac = 0, e1 = 0, e2 = 0;
        for (var k = 0; k < WIN - lag; k += 2) {
            var a = samples[off + k], b = samples[off + k + lag];
            ac += a * b; e1 += a * a; e2 += b * b;
        }
        var denom = Math.sqrt(e1 * e2);
        return denom > 1e-12 ? ac / denom : 0;
    }

    // Full-stride Goertzel (a 2-sample step with a full-rate coefficient
    // silently probes f/2 — the calibration-run-7 instrument bug).
    function bandE(samples, off, WIN, sr, f) {
        var wq = 2 * Math.PI * f / sr, cw = 2 * Math.cos(wq), s1 = 0, s2 = 0;
        for (var n = 0; n < WIN; n++) { var v = samples[off + n] + cw * s1 - s2; s2 = s1; s1 = v; }
        var m = s1 * s1 + s2 * s2 - cw * s1 * s2;
        return m > 0 ? m : 0;
    }

    function scoreVocal(f) {
        var s = 0;
        if (f.voicedRunShare >= 0.35) s += 2;
        else if (f.voicedRunShare >= 0.25) s += 1;
        if (f.pitchedRatio >= 0.6) s += 1;
        if (f.jitterCents >= 0.2 && f.jitterCents <= 30) s += 1;
        if (f.vibratoHz >= 4 && f.vibratoHz <= 8 && f.vibratoDepthCents >= 8 && f.vibratoDepthCents <= 120) s += 1;
        if (f.hfNoiseRatio >= 0.005) s += 1;
        if (f.chromaConc >= 0.2 && f.chromaConc <= 0.6) s += 1;
        if (f.chromaConc > 0.7) s -= 2;
        if (f.stableLongRuns > 0 && f.jitterCents < 0.1) s -= 2;
        if (f.harmonicRichness < 0.2 && f.jitterCents < 0.05 && f.vibratoDepthCents < 2) s -= 3;
        return s;
    }

    function isVocalVerdict(f) {
        return f.score >= 4 && f.pitchedRatio >= 0.45 && f.voicedRunShare >= 0.3;
    }

    // `rhythm` is exported so the second consumer (/audio-enhancer, whose
    // detector also measures hum / rolloff / cassette cutoff and therefore
    // cannot simply call analyze()) scores the music verdict off the SAME
    // rhythm evidence instead of keeping its own copy of the old silentRatio
    // scorer. One implementation, two callers.
    /* WHERE the model hears what: per patch of 16 kHz mono (0.96 s from the first
       sample) → { hopSec, rows: [[speech, music, audience, laughter], …] }, or
       null on a page without the second opinion. The buffer is transferred.
       crowd-removal.js asks it to keep the original where a voice speaks. */
    function listen(pcm16) {
        if (!deepConfig()) return Promise.resolve(null);
        return deepInit()
            .then(function () { return deepSend({ type: 'patches', pcm: pcm16 }, [pcm16.buffer]); })
            .then(function (r) { return (r && r.rows) ? { hopSec: r.hopSec, rows: r.rows } : null; });
    }

    window.ContentDetect = { analyze: analyze, rhythm: rhythmProbe, refine: refine, configureDeep: configureDeep, warmDeep: warmDeep, listen: listen, soundedStarts: soundedStarts };
})();
